import type { FC, ReactElement, ReactNode } from 'react';
import React, { useCallback } from 'react';

import type { BoxProps } from '@twilio-paste/core/box';
import { Box } from '@twilio-paste/core/box';
import type { PopoverStateReturnProps } from '@twilio-paste/core/popover';
import { Separator } from '@twilio-paste/core/separator';
import type { SideModalProps } from '@twilio-paste/core/side-modal';
import {
  SideModal,
  SideModalBody,
  SideModalContainer,
  SideModalFooter,
  SideModalFooterActions,
} from '@twilio-paste/core/side-modal';
import { Text as PasteText } from '@twilio-paste/core/text';
import { CloseIcon } from '@twilio-paste/icons/esm/CloseIcon';

import { DrawerContext, useDrawerContext } from './Drawer.context';

interface DrawerElementProps {
  children: NonNullable<ReactNode>;
}

interface HeaderProps {
  title: string;
  subtitle?: ReactElement | string;
  isDismissible?: boolean;
  as?: BoxProps['as'];
}

export const Header = ({ title, subtitle, as = 'h3', isDismissible = true }: HeaderProps) => {
  const state = useDrawerContext();

  const handleCloseClick = useCallback(() => {
    if (!isDismissible) {
      return;
    }

    state.hide();
  }, [state, isDismissible]);

  const heading = (
    <Box width="100%">
      <PasteText as={as} fontSize="fontSize60" fontWeight="fontWeightBold" marginBottom="space60">
        {title}
      </PasteText>

      {subtitle && (
        <PasteText
          as="p"
          color="colorTextDecorative10"
          fontSize="fontSize40"
          fontWeight="fontWeightNormal"
          marginBottom="space50"
        >
          {subtitle}
        </PasteText>
      )}
    </Box>
  );

  return (
    <Box paddingTop="space90" paddingLeft="space90" paddingRight="space90">
      <Box display="flex" justifyContent="space-between">
        {heading}
        <Box
          flex="0 0 20px"
          onClick={handleCloseClick}
          cursor={isDismissible ? 'pointer' : 'default'}
        >
          <CloseIcon
            decorative={false}
            size="sizeIcon20"
            title="Close drawer"
            color={isDismissible ? 'colorTextIcon' : 'colorTextWeaker'}
          />
        </Box>
      </Box>

      <Separator orientation="horizontal" />
    </Box>
  );
};

export const Content = ({ children }: DrawerElementProps) => (
  <SideModalBody>{children}</SideModalBody>
);

export const Footer = ({ children }: DrawerElementProps) => (
  <SideModalFooter>
    <SideModalFooterActions>{children}</SideModalFooterActions>
  </SideModalFooter>
);

interface DrawerComponentProps extends Pick<SideModalProps, 'aria-label'> {
  state: PopoverStateReturnProps;
  modal?: boolean;
  children: React.ReactNode;
}

interface DrawerComponent extends FC<DrawerComponentProps> {
  Content: typeof Content;
  Header: typeof Header;
  Footer: typeof Footer;
}

const DrawerOverlay = () => (
  <Box
    zIndex="zIndex70"
    position="fixed"
    top="0"
    right="0"
    bottom="0"
    left="0"
    backgroundColor="colorBackgroundOverlay"
  />
);

export const Drawer: DrawerComponent = ({ children, 'aria-label': label, state, modal = true }) => (
  <>
    {modal && state.visible && <DrawerOverlay />}

    <SideModalContainer state={state}>
      <SideModal aria-label={label}>
        <DrawerContext.Provider value={state}>{children}</DrawerContext.Provider>
      </SideModal>
    </SideModalContainer>
  </>
);

Drawer.Content = Content;
Drawer.Header = Header;
Drawer.Footer = Footer;
